
import Api from '@/Plugins/api'

// MODELS
import Order from '@/Models/Order'
import OrderContent from '@/Models/OrderContent'

// MODULES
import config from './config'
import dialogs from './dialogs'
import menu from './menu'
import modifiers from './modifiers'
import payment from './payment'

const MAX_OPEN_ORDERS = 200

const modules = {
  config,
  dialogs,
  menu,
  modifiers,
  payment,
}

const state = () => ({
  /**
   * This variable contains the open orders.
   * @type Array
   */
  open: [],

  /**
   * This variable points to the current order by reference.
   * @type Object
   */
  current: undefined,

  /**
   * Determine if the current order have been modified.
   * @type Boolean
   */
  isDirty: undefined,

  /**
   * This variable is used to prevent 2 clicks in a row.
   * @type Boolean
   */
  locked: false,

  /**
   * Holds the current dish order.
   * @type Number
   */
  dishOrder: 0,

  /**
   * Determine if should show a different style for paid order contents.
   * @type Boolean
   */
  showPaid: false,
})

const getters = {
  get: state => id => state.open.find(o => o.id == id),
  itemCounter: (state, getters, rootState, rootGetters) => payload => {
    if (typeof payload === 'number') {
      return state.current.count(payload)
    }
    const pivot = rootGetters['catalog/sellingFormatPivot'](payload)
    return pivot
      ? state.current.orderContents
        .filter(o => o.item_id == payload.item_id && o.format_pivot_id == pivot.id)
        .sum('quantity')
      : 0
  },
  getByTableId: state => id => state.open.find(o => o.table_id == id),
  config: (state, getters) => key => getters['config/get'](key),
  lines: state => state.current.lines,
  usesWeight: (state, getters, rootState, rootGetters) => {
    const usesWeight = o => !! rootGetters['catalog/item'](o.item_id)?.usesWeight

    return state.current.orderContents.some(
      content => usesWeight(content) || content.subContents.some(subContent => usesWeight(subContent))
    )
  },
}

const mutations = {
  set (state, v) {
    state.open = Array.isArray(v)
      ? v.slice(-MAX_OPEN_ORDERS)
      : []
  },

  current (state, order) {
    let index = state.open.findIndex(o => o.id == order.id)
    if (index === -1) {
      index = state.open.length
    }
    state.open.splice(index, 1, new Order(order)) // replace/push
    state.current = state.open[index]
    state.isDirty = false
    state.showPaid = false
  },

  remove (state, id) {
    let index = state.open.findIndex(o => o.id == id)
    if (index === -1) {
      return
    }
    state.open.splice(index, 1)
  },

  lock (state) {
    state.locked = true
  },
  unlock (state) {
    state.locked = false
  },

  dishOrder (state, v) {
    state.dishOrder = v
  },

  toggleShowPaid (state, v) {
    state.showPaid = ! state.showPaid
  },

  addContent (state, attributes) {
    state.current.add(attributes)
    state.current.recalculate()
    state.isDirty = true
  },
  updateContent (state, { index, attributes }) {
    state.current.orderContents[index].update(attributes)
    state.current.recalculate()
    state.isDirty = true
  },
  removeContent: (state, index) => {
    state.current.remove(index)
    state.current.recalculate()
    state.isDirty = true
  },
  removeContents (state) {
    state.current.unset()
    state.current.recalculate()
    state.isDirty = true
  },
  // splitContent (state, { index, quantity, invoice }) {
  //   state.current.split(index, quantity, invoice)
  //   state.isDirty = true
  // },
  splitContents (state, { indexes, invoice }) {
    Object.entries(indexes.countElements()).forEach(([index, quantity]) => {
      state.current.split(index, quantity, invoice)
    })
    state.isDirty = true
  },

  incrementContent (state, index) {
    state.current.orderContents[index].increment()
    state.current.recalculate()
    state.isDirty = true
  },
  decrementContent (state, index) {
    state.current.orderContents[index].decrement()
    state.current.recalculate()
    state.isDirty = true
  },

  removeSubContent (state, { orderContentIndex, subContentIndex }) {
    const orderContent = state.current.orderContents[orderContentIndex]
    const subContents = orderContent.subContents
    const id = subContents[subContentIndex].id
    if (id) {
      state.current.subContentsIdsToDelete.push(id)
    }
    subContents.splice(subContentIndex, 1)
    orderContent.recalculate()
    state.current.recalculate()
    state.isDirty = true
  },
  removeSubContentFromDatabase (state, id) {
    state.current.subContentsIdsToDelete.push(id)
  },

  applyDiscount (state, discount) {
    state.current.discount = discount
    state.current.recalculate()
    state.isDirty = true
  },
  cancelDiscount (state) {
    state.current.discount = null
    state.current.recalculate()
    state.isDirty = true
  },
  applyOrderDiscountToContents (state) {
    state.current.orderContents.forEach(content => {
      content.discount = state.current.discount
      content.recalculate()
    })
    state.current.discount = null
    state.current.recalculate()
    state.isDirty = true
  },

  changeSeats (state, v) {
    state.current.guests = v
    state.isDirty = true
  },

  changeStatus (state, v) {
    state.current.status = v
    state.isDirty = true
  },

  /** @deprecated */
  sendKitchenNote (state, id) {
    state.current.sendKitchenNote(id)
    state.isDirty = true
  },

  pendingAction (state, v) {
    if (typeof v === 'string') {
      v = { type: v }
    }
    state.current.addAction(v)
    state.isDirty = true
  },

  createInvoice (state, { data, customer }) {
    if (Object.keys(customer).length > 0) {
      customer = {
        customer_id: customer.id,
        customerJSON: JSON.parse(JSON.stringify(customer)),
      }
    }
    state.current.createInvoice({
      ...data,
      ...customer,
    })
  },
  setCurrentInvoiceToNotAccountable (state, mode) {
    state.current.getInvoice(mode).notAccountable = true
    state.isDirty = true
  },
  addPayment (state, { mode, payment }) {
    state.current.getInvoice(mode).addPayment(payment)
    state.current.alreadyPaid += payment.payAmount
    state.isDirty = true
  },
  linkInvoicesWithContents (state) {
    state.current.linkInvoicesWithContents()
    state.isDirty = true
  },
  invoiceSummary (state, invoice) {
    invoice.setSummary()
    state.isDirty = true
  },

  forceSave (state) {
    state.isDirty = true
  },
  close (state) {
    state.current.close()
    state.isDirty = true
  },
}

const actions = {
  sync ({ commit, dispatch }, orders) {
    commit('set', orders)
    dispatch('freeDirectSaleEmptyOrders')
  },
  _addContent ({ state, commit, getters, rootGetters }, { item, modifiers }) {
    commit('addContent', {
      item_id: item.id,
      itemName: item.name,
      itemPrice: item.price,
      modifiers: [ ...modifiers, ...getters['modifiers/hidden'](item)],
      format_pivot_id: item.sellingFormatPivotId ?? null,
      orderContentCombination: item.combination ?? null,
      dishOrder: rootGetters['catalog/itemDishOrder'](item)?.order ?? state.dishOrder,
    })
  },
  addItems ({ state, commit, dispatch }, items) {
    if (state.locked) {
      return
    }
    commit('lock')
    items.forEach(item => dispatch('_addContent', {
      item,
      modifiers: [],
    }))
    setTimeout(() => commit('unlock'), 100)
  },
  addItem ({ dispatch }, item) {
    dispatch('addItems', [item])
  },
  addItemWithModifiers ({ state, dispatch, getters }) {
    dispatch('_addContent', {
      item: state.modifiers.item,
      modifiers: getters['modifiers/selected'],
    })
  },
  async addMenu ({ state, commit, dispatch, rootState, rootGetters }) {
    const menu = rootState.orders.menu
    const { subContents, extraContents } = await dispatch('_applySpecialPriceRate', menu.contents)
    let menuPrice = menu.item.price
    if (extraContents.length) {
      menuPrice -= extraContents.sum('total') / menu.quantity
    }
    commit('addContent', {
      item_id: menu.item.id,
      itemName: menu.item.name,
      itemPrice: menuPrice,
      quantity: menu.quantity,
      subContents,
      dishOrder: rootGetters['catalog/itemDishOrder'](menu.item)?.order ?? state.dishOrder,
    })
    extraContents.forEach(content => commit('addContent', content))
  },
  _applySpecialPriceRate ({ rootState, rootGetters }, contents) {
    const menuPriceRateId = rootGetters['fiscalCountry/config']('menu_price_rate_id')
    if (! menuPriceRateId) {
      return { subContents: contents, extraContents: [] }
    }
    const menuPriceRate = rootState.rummage.data.TablePrice?.find(o => !! o.active && o.id == menuPriceRateId)
    if (! menuPriceRate) {
      return { subContents: contents, extraContents: [] }
    }
    const alcoholContents = rootState.rummage.data.Price?.filter(o => o.table_price_id == menuPriceRateId && o.priceable_type === 'App\\Models\\Menu\\MenuItem')
    if (! alcoholContents.length) {
      return { subContents: contents, extraContents: [] }
    }
    const subContents = []
    const extraContents = []
    contents.forEach(content => {
      const price = alcoholContents.find(o => o.priceable_id == content.item_id)?.price
      if (! price) {
        subContents.push(content)
        return
      }
      extraContents.push(new OrderContent({
        ...content,
        price_id: parseInt(menuPriceRateId),
        itemPrice: parseFloat(price),
      }))
    })
    return { subContents, extraContents }
  },
  free ({ state, commit, dispatch, rootGetters }, order = null) {
    if (order !== null) {
      commit('current', order)
    }
    dispatch('log', 'Releasing...')
    return Api('setFree2Order', {
      id: state.current.id,
      warehouse_id: rootGetters['rummage/cashier'].warehouse_id,
      orderContentsIdsToDelete: state.current.orderContentsIdsToDelete,
    })
      .then(r => {
        dispatch('log', 'Released')
        commit('remove', r.data?.id)
      })
  },
  async freeDirectSaleEmptyOrders ({ state, dispatch, rootState }) {
    const orders = state.open
      .filter(o => o.lockedBy === rootState.UUID && o.orderContents.length === 0 && o.table_id === null)

    for (const order of orders) {
      await dispatch('free', order)
    }
  },
  async stash ({ state, commit, dispatch, rootState, rootGetters }, lock = false) {
    dispatch('loading/start', 400, { root: true })
    commit('lock')
    dispatch('log', 'Stashing...')
    return Api('lockOrder', {
      orderId: state.current.id,
      uuid: rootState.UUID,
    })
      .then(r => {
        if (! r.data?.success) {
          const deviceName = r.data?.deviceName ?? 'Unknown'
          dispatch('log', 'Locked by ' + deviceName)
          const message = rootGetters['translate']('lockedBy') ?? 'lockedBy'
          dispatch('alert/showError', message + ': ' + deviceName, { root: true })
          commit('unlock')
          commit('loading/stop', null, { root: true })
          return
        }
        commit('linkInvoicesWithContents')
        const warehouse_id = rootGetters['rummage/cashier'].warehouse_id
        return Api(['save', 'Order'], {
          ...state.current,
          saveToken: String.random(32),
          lockedBy: lock ? rootState.UUID : null,
          ...(warehouse_id ? { warehouse_id } : {}),
        })
          .then(r => dispatch('log', 'Stashed'))
          .catch(e => dispatch('xError', e, { root: true }))
          .finally(() => {
            commit('unlock')
            commit('loading/stop', null, { root: true })
          })
      })
      .catch(e => {
        commit('unlock')
        commit('loading/stop', null, { root: true })
        dispatch('xError', e, { root: true })
      })
  },
  log ({ state, dispatch }, message) {
    dispatch('log', {
      action: message,
      model: 'Order',
      model_id: state.current.id,
    }, { root: true })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules
}
